/**
 * What the section nav shows next to a section title.
 *
 * RC-2 (820 sweep D-04/D-16). This rule used to live inline in the nav JSX,
 * where it was untestable and where it shipped a real defect: the missing-count
 * was gated on `progress.touched`, which is VALUE-derived, so a section the
 * client had never opened carried no badge at all. An untouched section was
 * therefore indistinguishable from a finished one, and a client could reach the
 * end of a sixteen-section form, see nothing flagged anywhere, and submit it
 * mostly empty.
 *
 * Extracted here so the rule is pinned by tests rather than by eyeballing a
 * preview. Decision (Tzvi, 2026-07-24): an untouched section says "Not started"
 * in neutral grey; the amber missing-count waits until the client has actually
 * entered something, so a brand new form does not open as a wall of warnings.
 */
import type { SectionProgress } from './validateForm';

export type SectionBadge =
  | { kind: 'none' }
  | { kind: 'not-started' }
  | { kind: 'missing'; count: number };

const NONE: SectionBadge = { kind: 'none' };

export function sectionBadge(
  progress: SectionProgress | undefined,
  isContextOnly: boolean,
): SectionBadge {
  // No progress computed yet (pre-hydration): show nothing rather than guess.
  if (!progress) return NONE;

  // Cross-form context sections are read-only data owned by another form. The
  // client cannot author them, so neither a count nor a "not started" nudge
  // means anything to them.
  if (isContextOnly) return NONE;

  if (progress.complete) return NONE;

  // The load-bearing branch: never been opened, so say so.
  if (!progress.touched) return { kind: 'not-started' };

  if (progress.missingRequired > 0) {
    return { kind: 'missing', count: progress.missingRequired };
  }

  // Started, nothing required outstanding, but not `complete` either. That is
  // a section with no required fields at all (`complete` needs totalRequired
  // > 0), so there is nothing to chase.
  return NONE;
}
